import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Video } from '@/lib/api/types';
import { formatDuration, formatFileSize } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { 
  Play, 
  Clock, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Search,
  FileText,
  Users,
  Eye,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import VideoProcessingStatus from './VideoProcessingStatus';
import { VideoApi } from '@/lib/api/videoApi';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface UnifiedVideoCardProps {
  video: Video;
  className?: string;
}

export default function UnifiedVideoCard({ video, className }: UnifiedVideoCardProps) {
  const [showProcessingDetails, setShowProcessingDetails] = useState(false);
  const [isReindexing, setIsReindexing] = useState(false);
  const { session } = useAuth();
  const { toast } = useToast();
  const videoApi = new VideoApi();

  const getStatusIcon = () => {
    switch (video.status) {
      case 'COMPLETED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'PROCESSING':
      case 'RETRY':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSearchIndexingIcon = () => {
    switch (video.search_indexed) {
      case 'true':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Search className="h-4 w-4 text-gray-500" />;
    }
  };

  const getProcessingFeatures = () => {
    const features = [];
    
    if (video.transcript_available === 'true') {
      features.push({ icon: <FileText className="h-3 w-3" />, label: 'Transcript', color: 'text-green-600' });
    }
    if (video.speakers_detected === 'true') {
      features.push({ icon: <Users className="h-3 w-3" />, label: 'Speakers', color: 'text-blue-600' });
    }
    if (video.objects_detected === 'true') {
      features.push({ icon: <Eye className="h-3 w-3" />, label: 'Objects', color: 'text-purple-600' });
    }
    
    return features;
  };

  const handleReindex = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!session?.access_token) {
      toast({
        title: "Authentication Error",
        description: "Please log in to re-index this video.",
        variant: "destructive",
      });
      return;
    }

    if (video.status !== 'COMPLETED') {
      toast({
        title: "Cannot Index Yet",
        description: "Video must be fully processed before search indexing can begin.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsReindexing(true);
      await videoApi.indexVideoForSearch(video.id, session.access_token);
      
      toast({
        title: "Search Indexing Started",
        description: "Search indexing has been initiated. This may take a few minutes to complete.",
      });
      
      // Refresh the page after a short delay to show updated status
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('Re-indexing failed:', error);
      toast({
        title: "Indexing Failed",
        description: error instanceof Error ? error.message : "Failed to start search indexing. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsReindexing(false);
    }
  };

  return (
    <Card className={cn("overflow-hidden transition-all duration-300 hover:shadow-lg", className)}>
      <Link to={`/videos/${video.id}`} className="block">
        <div className="relative group">
          <img 
            src={video.thumbnail_url || '/placeholder.svg'} 
            alt={video.title} 
            className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
            <div className="rounded-full bg-white/80 p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-0 group-hover:scale-100">
              <Play className="h-5 w-5 text-video-blue" />
            </div>
          </div>
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
            {video.duration ? formatDuration(video.duration) : 'Unknown'}
          </div>
        </div>
      </Link>
      
      <CardContent className="p-4">
        <CardHeader className="p-0 pb-3">
          <CardTitle className="text-lg line-clamp-2 group-hover:text-video-blue transition-colors">
            <Link to={`/videos/${video.id}`}>
              {video.title}
            </Link>
          </CardTitle>
        </CardHeader>
        
        {/* Status Summary */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <span className="font-medium">Processing</span>
            </div>
            <Badge variant={video.status === 'COMPLETED' ? 'default' : 'secondary'}>
              {video.status}
            </Badge>
          </div>
          
          {video.status === 'COMPLETED' && video.search_indexed === 'false' && (
            <div className="mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3" />
                <span>Processing complete! Ready to enable search indexing.</span>
              </div>
            </div>
          )}
          
          {video.status === 'PENDING' && (
            <div className="mt-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>Video is being processed. This may take several minutes.</span>
              </div>
            </div>
          )}
        </div>
        
        <div className="space-y-3">
          {/* Video Info */}
          <div className="text-sm text-muted-foreground space-y-1">
            <div className="flex justify-between">
              <span>File Name:</span>
              <span className="font-mono text-xs truncate max-w-32" title={video.file_name}>
                {video.file_name}
              </span>
            </div>
            <div className="flex justify-between">
              <span>File Size:</span>
              <span>{formatFileSize(video.file_size)}</span>
            </div>
            {video.duration && (
              <div className="flex justify-between">
                <span>Duration:</span>
                <span>{formatDuration(video.duration)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Uploaded:</span>
              <span>{new Date(video.created_at).toLocaleDateString()}</span>
            </div>
            {video.processing_started_at && (
              <div className="flex justify-between">
                <span>Processing Started:</span>
                <span>{new Date(video.processing_started_at).toLocaleDateString()}</span>
              </div>
            )}
            {video.processing_completed_at && (
              <div className="flex justify-between">
                <span>Processing Completed:</span>
                <span>{new Date(video.processing_completed_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {/* Processing Status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getStatusIcon()}
                <span className="text-sm font-medium">Processing</span>
              </div>
              <Badge variant={video.status === 'COMPLETED' ? 'default' : 'secondary'}>
                {video.status}
              </Badge>
            </div>
            
            {video.status === 'PROCESSING' && (
              <div className="space-y-1">
                <Progress value={video.processing_progress} className="h-2" />
                <div className="text-xs text-muted-foreground text-center">
                  {video.processing_progress}% complete
                </div>
              </div>
            )}
            
            {video.processing_attempts > 0 && (
              <div className="text-xs text-muted-foreground">
                Attempts: {video.processing_attempts}
              </div>
            )}
            
            {video.processing_error && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span className="line-clamp-1">{video.processing_error}</span>
              </div>
            )}
          </div>

          {/* Search Indexing Status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getSearchIndexingIcon()}
                <span className="text-sm font-medium">Search Indexing</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={video.search_indexed === 'true' ? 'default' : 'secondary'}>
                  {video.search_indexed === 'true' ? 'Indexed' :
                   video.search_indexed === 'processing' ? 'Indexing' :
                   video.search_indexed === 'failed' ? 'Failed' : 'Not Indexed'}
                </Badge>
                {(video.search_indexed === 'false' || video.search_indexed === 'failed') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleReindex}
                          disabled={isReindexing}
                          className="h-6 px-2 text-xs"
                        >
                          {isReindexing ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : (
                            <Search className="h-3 w-3" />
                          )}
                          {isReindexing ? 'Indexing...' : 'Re-index'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Start search indexing for this video</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
            
            {video.search_indexed === 'true' && (
              <div className="text-xs text-muted-foreground space-y-1">
                <div>Frames: {video.frame_count}</div>
                <div>Segments: {video.segment_count}</div>
                {video.search_indexed_at && (
                  <div>Indexed: {new Date(video.search_indexed_at).toLocaleDateString()}</div>
                )}
                <div className="text-green-600 font-medium">✓ Search enabled</div>
              </div>
            )}
            
            {video.search_indexed === 'processing' && (
              <div className="text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Creating search indices...
                </div>
              </div>
            )}
            
            {video.search_indexed === 'failed' && (
              <div className="text-xs text-red-600">
                <div className="flex items-center gap-1">
                  <XCircle className="h-3 w-3" />
                  Search indexing failed
                </div>
              </div>
            )}
            
            {video.search_indexed === 'false' && video.status === 'COMPLETED' && (
              <div className="text-xs text-amber-600">
                <div className="flex items-center gap-1">
                  <Search className="h-3 w-3" />
                  Ready to enable search
                </div>
              </div>
            )}
          </div>

          {/* Processing Features */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Available Features</div>
            <div className="flex flex-wrap gap-1">
              {video.transcript_available === 'true' && (
                <Badge variant="outline" className="text-green-600">
                  <FileText className="h-3 w-3" />
                  <span className="ml-1">Transcript</span>
                </Badge>
              )}
              {video.speakers_detected === 'true' && (
                <Badge variant="outline" className="text-blue-600">
                  <Users className="h-3 w-3" />
                  <span className="ml-1">Speakers</span>
                </Badge>
              )}
              {video.objects_detected === 'true' && (
                <Badge variant="outline" className="text-purple-600">
                  <Eye className="h-3 w-3" />
                  <span className="ml-1">Objects</span>
                </Badge>
              )}
              {video.transcript_available !== 'true' && 
               video.speakers_detected !== 'true' && 
               video.objects_detected !== 'true' && (
                <div className="text-xs text-muted-foreground">
                  {video.status === 'COMPLETED' ? (
                    <div className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Features not detected during processing
                    </div>
                  ) : (
                    'No features available yet'
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                setShowProcessingDetails(!showProcessingDetails);
              }}
              className="flex-1"
            >
              {showProcessingDetails ? 'Hide' : 'Show'} Details
            </Button>
            {(video.search_indexed === 'false' || video.search_indexed === 'failed') && video.status === 'COMPLETED' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleReindex}
                      disabled={isReindexing}
                      className="flex-1"
                    >
                      {isReindexing ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      {isReindexing ? 'Indexing...' : (video.search_indexed === 'failed' ? 'Retry Search Index' : 'Enable Search')}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{video.search_indexed === 'failed' ? 'Retry search indexing for this video' : 'Enable search functionality for this video'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        {/* Detailed Processing Status */}
        {showProcessingDetails && (
          <div className="mt-4 pt-4 border-t bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-sm text-gray-700 mb-3">Processing Details</h4>
            <VideoProcessingStatus 
              videoId={video.id}
              onStatusChange={(status) => {
                // Update the video status if needed
                console.log('Video status changed:', status);
              }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
} 